/**
 * Relationship Compass — Coaching Privacy Dashboard (Compass)
 *
 * Styled to the app's dark theme, with room for the fixed 80 px header.
 * Coaching data belongs to the person's DDD account, so a signed-out
 * visitor is asked to log in.
 *
 * Australian English throughout.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, Trash2, Shield, AlertTriangle } from 'lucide-react';
import coachingService from '../services/CoachingService';
import { useAuth } from '../context/AuthContext';
import { downloadFile, downloadReadableTranscript } from '@ddd/coaching-export';
import { clearModuleContext } from '../services/coachModuleContext';

const COUNTRIES = [
  { code: 'AU', label: 'Australia' }, { code: 'NZ', label: 'New Zealand' },
  { code: 'IN', label: 'India' }, { code: 'UK', label: 'United Kingdom' },
  { code: 'IE', label: 'Ireland' }, { code: 'CA', label: 'Canada' },
  { code: 'US', label: 'United States' }
];

const PAGE = 'min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pt-32 pb-16 px-4';
const CARD = 'bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-5 mb-5';
const FIELD = 'mt-1 w-full rounded-lg border border-slate-600 bg-slate-900 text-slate-100 px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500';

export default function CoachingPrivacyPage() {
  const { user, redirectToAuth } = useAuth();
  const signedIn = !!user && !user.isAnonymous;
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (!signedIn) {
      setLoading(false);
      return;
    }
    (async () => {
      const res = await coachingService.getProfile();
      if (res?.success) setProfile(res.profile);
      setLoading(false);
    })();
  }, [signedIn]);

  const updatePersonalContext = async (patch) => {
    setSaving(true);
    const res = await coachingService.updateProfile({ personalContext: { ...(profile?.personalContext || {}), ...patch } });
    if (res?.success) { setProfile(res.profile); setMessage({ kind: 'success', text: 'Saved.' }); }
    else { setMessage({ kind: 'error', text: res?.error || 'Save failed.' }); }
    setSaving(false);
    setTimeout(() => setMessage(null), 2500);
  };

  // The readable transcript leads: what was actually said, as Markdown. Raw
  // Firestore JSON stays behind a quiet secondary link for portability.
  const exportData = async (raw = false) => {
    const res = await coachingService.exportData();
    if (!res?.success) { setMessage({ kind: 'error', text: 'Export failed.' }); return; }
    if (raw) {
      downloadFile(JSON.stringify(res, null, 2), `ddd-coaching-${new Date().toISOString().slice(0, 10)}.json`, 'application/json');
    } else {
      downloadReadableTranscript(res, { coachName: 'Compass', appId: 'relationship-compass', appName: 'Relationship Compass' });
    }
    setMessage({ kind: 'success', text: 'Downloaded.' });
    setTimeout(() => setMessage(null), 2500);
  };

  const deleteAll = async () => {
    const res = await coachingService.deleteAll();
    if (res?.success) { clearModuleContext(); setProfile(null); setDeleted(true); setConfirmDelete(false); }
    else { setMessage({ kind: 'error', text: 'Delete failed. Please try again.' }); }
  };

  if (loading) return <div className={PAGE}><p className="text-center text-slate-400 mt-16">Loading…</p></div>;

  if (!signedIn) {
    return (
      <div className={PAGE}>
        <div className="max-w-2xl mx-auto">
          <h1 className="text-2xl font-bold text-white mb-2">Your coaching data</h1>
          <p className="text-slate-300 mb-6">Coaching data is kept with your Dive Deep Dialogues account. Log in to see, download or delete it.</p>
          <button type="button" onClick={() => redirectToAuth('signin')} className="px-6 py-3 bg-rose-600 hover:bg-rose-700 text-white rounded-full font-medium transition-colors">Log in</button>
        </div>
      </div>
    );
  }

  if (deleted) {
    return (
      <div className={PAGE}>
        <div className="max-w-2xl mx-auto">
          <h1 className="text-2xl font-bold text-white mb-2">Coaching data deleted</h1>
          <p className="text-slate-300">Your coaching profile and all sessions have been permanently removed. You can start fresh anytime from <Link to="/coach" className="text-rose-300 underline">Coach</Link>.</p>
        </div>
      </div>
    );
  }

  if (!profile?.consent?.granted) {
    return (
      <div className={PAGE}>
        <div className="max-w-2xl mx-auto">
          <h1 className="text-2xl font-bold text-white mb-2">No coaching data yet</h1>
          <p className="text-slate-300">You haven't started coaching yet, so there's nothing stored. Head to <Link to="/coach" className="text-rose-300 underline">Coach</Link> when you're ready.</p>
        </div>
      </div>
    );
  }

  const pc = profile.personalContext || {};
  const apps = profile.apps || {};

  return (
    <div className={PAGE}>
    <div className="max-w-3xl mx-auto">
      <Link to="/coach" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-4">
        <ArrowLeft size={16} aria-hidden="true" /> Back to coaching
      </Link>
      <div className="flex items-center gap-3 mb-6">
        <Shield className="text-rose-400" size={28} aria-hidden="true" />
        <h1 className="text-2xl font-bold text-white">Your coaching data</h1>
      </div>
      <p className="text-slate-300 mb-6 leading-relaxed">
        This is everything Compass knows about you. You can change what's stored, download your conversations,
        or delete it all. What the coach remembers is stored in Australia. To write each reply, your message is sent to Anthropic&apos;s Claude, which doesn&apos;t use it to train AI. We never sell your data.
      </p>

      {message && (
        <div className={`mb-4 px-4 py-2 rounded-lg text-sm ${message.kind === 'success' ? 'bg-green-900/30 border border-green-500/30 text-green-200' : 'bg-red-900/30 border border-red-500/30 text-red-200'}`}>{message.text}</div>
      )}

      <section className={CARD}>
        <h2 className="text-lg font-semibold text-white mb-3">Your profile</h2>
        <div className="space-y-3">
          <label className="block">
            <span className="text-sm font-medium text-slate-200">Name</span>
            <input type="text" defaultValue={pc.firstName || ''} onBlur={(e) => { const v = e.target.value.trim(); if (v !== (pc.firstName || '')) updatePersonalContext({ firstName: v }); }}
              className={FIELD} maxLength={40} />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-slate-200">Country (for support resources)</span>
            <select defaultValue={pc.country || ''} onChange={(e) => updatePersonalContext({ country: e.target.value || null })} className={FIELD}>
              <option value="">Not set</option>
              {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-sm font-medium text-slate-200">Coaching style</span>
            <select defaultValue={pc.preferredCoachingStyle || 'supportive'} onChange={(e) => updatePersonalContext({ preferredCoachingStyle: e.target.value })} className={FIELD}>
              <option value="supportive">Supportive</option>
              <option value="facilitative">Facilitative</option>
              <option value="directive">Directive</option>
              <option value="challenging">Challenging</option>
            </select>
          </label>
        </div>
      </section>

      {Object.entries(apps).map(([appId, app]) => (
        <section key={appId} className={CARD}>
          <h2 className="text-lg font-semibold text-white mb-3">What Compass remembers</h2>
          {app.activeGoals?.length > 0 && (
            <div className="mb-4"><h3 className="text-sm font-medium text-slate-200 mb-1">Active goals</h3>
              <ul className="list-disc pl-5 text-sm text-slate-300 space-y-1">{app.activeGoals.map((g, i) => <li key={i}>{g.description}</li>)}</ul></div>
          )}
          {app.knownStruggles?.length > 0 && (
            <div className="mb-4"><h3 className="text-sm font-medium text-slate-200 mb-1">Known struggles</h3>
              <ul className="list-disc pl-5 text-sm text-slate-300 space-y-1">{app.knownStruggles.map((s, i) => <li key={i}>{s.topic || s}</li>)}</ul></div>
          )}
          {app.relationshipMap && Object.keys(app.relationshipMap).length > 0 && (
            <div className="mb-4"><h3 className="text-sm font-medium text-slate-200 mb-1">People you've mentioned</h3>
              <ul className="text-sm text-slate-300 space-y-1">{Object.entries(app.relationshipMap).map(([role, data]) => (
                <li key={role}><span className="font-medium">{role}:</span> {data?.name || 'unnamed'}{data?.notes && <span className="text-slate-400"> ({data.notes})</span>}</li>
              ))}</ul></div>
          )}
          {app.patternsNoticed?.length > 0 && (
            <div className="mb-4"><h3 className="text-sm font-medium text-slate-200 mb-1">Patterns noticed</h3>
              <ul className="list-disc pl-5 text-sm text-slate-300 space-y-1">{app.patternsNoticed.map((p, i) => <li key={i}>{p}</li>)}</ul></div>
          )}
          {!app.activeGoals?.length && !app.knownStruggles?.length && !Object.keys(app.relationshipMap || {}).length && !app.patternsNoticed?.length && (
            <p className="text-sm text-slate-400 italic">Nothing remembered yet. It builds up as you have sessions.</p>
          )}
        </section>
      ))}

      <section className={CARD}>
        <h2 className="text-lg font-semibold text-white mb-3">Export or delete</h2>
        <button onClick={() => exportData(false)} className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm font-medium text-white">
          <Download size={16} /> Download my conversations
        </button>
        <button onClick={() => exportData(true)} className="block mt-2 mb-3 text-xs text-slate-400 hover:text-slate-200 underline">
          Or download the full technical file (JSON)
        </button>
        {!confirmDelete ? (
          <button onClick={() => setConfirmDelete(true)} className="flex items-center gap-2 px-4 py-2 border border-red-500/50 text-red-300 hover:bg-red-900/30 rounded-lg text-sm font-medium">
            <Trash2 size={16} /> Delete all coaching data
          </button>
        ) : (
          <div className="border border-red-500/40 bg-red-900/20 rounded-lg p-4">
            <div className="flex items-start gap-2 mb-3">
              <AlertTriangle className="text-red-400 mt-0.5 flex-shrink-0" size={18} aria-hidden="true" />
              <p className="text-sm text-red-200">This permanently deletes your coaching profile, all session summaries, and everything Compass remembers. Can't be undone.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={deleteAll} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">Yes, delete everything</button>
              <button onClick={() => setConfirmDelete(false)} className="px-4 py-2 text-slate-400 hover:text-white text-sm">Cancel</button>
            </div>
          </div>
        )}
      </section>
      <p className="text-xs text-slate-400 italic">{saving && 'Saving…'}</p>
    </div>
    </div>
  );
}
